import {
	Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges,
	SimpleChange, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, QueryList,
	ViewChild, ViewChildren, ElementRef
} from '@angular/core';
import { Device } from 'DiscoveryEngine.TS/Core';
import { Table } from 'primeng/table';
import { IfEntry } from 'DiscoveryEngine.TS/InfoSets/IfTable';
import { BridgePortEntry } from 'DiscoveryEngine.TS/InfoSets/BridgePortTable';
import { VlanEntry } from 'DiscoveryEngine.TS/InfoSets/VlanTable';
import { TableUtils } from 'common';


export class BridgePortSelectorEntry {
	public dataKey: string;
	public ifIndex: string;
	public name: string;
	public description: string;
	public operStatus: string;
	public vlanIndex: string;
	public vlanName: string;
	public trunkedVLANs: string;
	public connectedDevices: string;

	public ifIndexTag: number;
	public operStatusTag: number;
	public vlanIndexTag: number;

	public interfaceTag: IfEntry;
	public get hasInterfaceTag(): boolean {
		return !!this.interfaceTag;
	}

	public portTag: BridgePortEntry;
	public get hasPortTag(): boolean {
		return !!this.portTag;
	}

	public vlanTag: VlanEntry;
	public get hasVlanTag(): boolean {
		return !!this.vlanTag;
	}

	constructor() {
		this.dataKey = "";

		this.ifIndex = "";
		this.name = "";
		this.description = "";
		this.operStatus = "";
		this.vlanIndex = "";
		this.vlanName = "";
		this.trunkedVLANs = "";
		this.connectedDevices = "";

		this.ifIndexTag = -1;
		this.operStatusTag = -1;
		this.vlanIndexTag = -1;
		this.interfaceTag = null;
		this.portTag = null;
		this.vlanTag = null;
	}
}


@Component({
	selector: 'bridgeport-selector',
	templateUrl: './bridgeport-selector.component.html',
	styleUrls: ['./bridgeport-selector.component.scss'],
	//changeDetection: ChangeDetectionStrategy.OnPush
})
export class BridgePortSelectorComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	public device: Device;

	@Input()
	public selectionMode: string;

	@Input()
	public selection: BridgePortEntry[];

	@Input()
	public portTableHeightVh: number;

	@Input()
	public portTableHeightPx: number;

	@Input()
	public portTableMinHeight: number;

	@Output()
	public selectionChange: EventEmitter<BridgePortEntry[]>;

	@ViewChild('table', { static: true })
	public table: Table;

	@ViewChildren('filter')
	public filters: QueryList<ElementRef<HTMLInputElement>>;

	public cols: any[];
	public entries: BridgePortSelectorEntry[];

	public selectedPorts: any;

	public first: number;

	constructor(private changeDetector: ChangeDetectorRef) {

		this.device = null;
		this.selectionMode = "single";
		this.selection = [];
		this.selectionChange = new EventEmitter<BridgePortEntry[]>();
		this.cols = [
			{ field: 'ifIndex', header: 'IF Index', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'name', header: 'Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'description', header: 'Description', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'operStatus', header: 'Oper Status', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'vlanIndex', header: 'VLAN Index', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'vlanName', header: 'VLAN Name', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'trunkedVLANs', header: 'Trunked VLAN(s)', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
			{ field: 'connectedDevices', header: 'Connected Devices', sortable: "custom", sortFunction: (event) => this.doCustomSort(event), filterMatchMode: "contains" },
		];

		this.entries = [];

		this.clearSelectedPorts();

		this.first = 0;
	}

	public get portTableHeight(): string {
		let result = `max(calc(${this.portTableHeightVh}vh + ${this.portTableHeightPx}px), ${this.portTableMinHeight}px)`;
		//console.log(result);
		return result;
	}

	public doCustomSort(event): void {
		let comparer = function (a: BridgePortSelectorEntry, b: BridgePortSelectorEntry): number {
			let result = 0;

			switch (event.field) {
				case 'ifIndex':
					if (a.ifIndexTag < b.ifIndexTag) {
						result = -1;
					}
					else if (a.ifIndexTag > b.ifIndexTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				case 'operStatus':
					if (a.operStatusTag < b.operStatusTag) {
						result = -1;
					}
					else if (a.operStatusTag > b.operStatusTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				case 'vlanIndex':
					if (a.vlanIndexTag < b.vlanIndexTag) {
						result = -1;
					}
					else if (a.vlanIndexTag > b.vlanIndexTag) {
						result = 1;
					}
					else {
						result = 0;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	public ngOnInit(): void {

	}

	public onFilterChange(): void {
		this.clearSelectedPorts();
	}

	private clearSelectedPorts(): void {
		this.selectedPorts = (this.selectionMode == "single" ? undefined : []);

		this.selection = [];
		this.selectionChange.emit(this.selection);
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (!!changes["device"] || !!changes["selectionMode"]) {

			this.clearSelectedPorts();

			this.first = 0;

			this.rebuildEntries();

			if (this.filters) {
				this.filters.forEach((filter: ElementRef<HTMLInputElement>) => {
					filter.nativeElement.value = "";
				});
			}

			this.cols.forEach((col) => {
				this.table.filter("", col.field, col.filterMatchMode)
			});
		}
	}

	public ngOnDestroy(): void {

	}

	public rebuildEntries(): void {

		let ports: BridgePortEntry[] = [];
		let interfaces: IfEntry[] = [];
		let vlans: VlanEntry[] = [];

		if (!!this.device && this.device.HasBridgePortTable && this.device.HasIfTable && this.device.HasVlanTable) {

			let vlanTable = this.device.VlanTable;
			let isTrunk = false;

			let bridgePorts = this.device.BridgePortTable.ToArray();
			bridgePorts.forEach((bpEntry: BridgePortEntry) => {
				isTrunk = false;

				//if (bpEntry.TrunkedVLANsString) {
				//	if (bpEntry.TrunkedVLANsString.includes(',')) {
				//		isTrunk = true;
				//	}
				//}

				if (isTrunk === false) {
					let ifEntry: IfEntry = this.device.IfTable.Find(bpEntry.IfIndex);
					if (ifEntry) {

						let vlanEntry: VlanEntry = null;
						if (bpEntry.HasVlanIndex) {
							vlanEntry = this.device.VlanTable.Find(bpEntry.VlanIndex);
							if (!vlanEntry) {
								console.error(`Missing bridge port VLAN: ${this.device.DisplayName} - ${bpEntry.VlanIndex}`);
							}
						}

						ports.push(bpEntry);
						interfaces.push(ifEntry);
						vlans.push(vlanEntry);
					}
				}
			});
		}

		let selectedPortIndexes = new Set();
		this.getSelectedPorts().forEach((bpEntry: BridgePortEntry) => selectedPortIndexes.add(bpEntry.Index));

		this.selectedPorts = undefined;
		this.entries = [];

		for (let i = 0; i < ports.length; ++i) {
			let bpEntry = ports[i];
			let ifEntry = interfaces[i];
			let vlanEntry = vlans[i];

			let entry = new BridgePortSelectorEntry();

			entry.dataKey = bpEntry.Index.toString();
			entry.ifIndex = ifEntry.IfIndex.toString();
			entry.name = ifEntry.Name;
			entry.description = ifEntry.Description;
			entry.operStatus = ifEntry.OperStatusToString();

			if (bpEntry.HasVlanIndex) {
				entry.vlanIndex = bpEntry.VlanIndex.toString();
				entry.vlanIndexTag = bpEntry.VlanIndex;
			}
			else {
				entry.vlanIndex = "";
				entry.vlanIndexTag = -1;
			}

			if (vlanEntry) {
				entry.vlanName = vlanEntry.DisplayName;
			}
			else {
				entry.vlanName = "";
			}

			//entry.connectedDevices = ifEntry.ConnectedDevices;
			entry.connectedDevices = bpEntry.ConnectedDevices;
			entry.trunkedVLANs = bpEntry.TrunkedVLANsString;

			entry.ifIndexTag = ifEntry.IfIndex;
			entry.operStatusTag = ifEntry.OperStatus;
			entry.portTag = bpEntry;
			entry.interfaceTag = ifEntry;
			entry.vlanTag = vlanEntry;

			this.entries.push(entry);

			if (selectedPortIndexes.has(bpEntry.IfIndex)) {
				// Add interface to selection
				switch (this.selectionMode) {
					case "single":
						this.selectedPorts = entry;
						break;
					case "multiple":
						if (this.selectedPorts === undefined) {
							this.selectedPorts = [entry];
						}
						else {
							this.selectedPorts.push(entry);
						}
						break;
					default:
						console.assert(false);
						break;
				}
			}
		}

		this.selection = this.getSelectedPorts();
		this.selectionChange.emit(this.selection);

		// Set column widths
		let colPropNames: string[] = [
			"ifIndex", "name", "description", "operStatus", "vlanIndex", "vlanName", "trunkedVLANs", "connectedDevices"
		];

		TableUtils.setColumnWidths(colPropNames, this.entries, this.cols);

		// Check for changes
		//this.changeDetector.markForCheck();
		this.changeDetector.detectChanges();
	}

	public onSelectionChange(event): void {
		this.selection = this.getSelectedPorts();
		this.selectionChange.emit(this.selection);
	}

	private getSelectedPorts(): BridgePortEntry[] {
		let result: BridgePortEntry[] = [];
		if (this.selectedPorts) {
			if (Array.isArray(this.selectedPorts)) {
				this.selectedPorts.forEach((entry: BridgePortSelectorEntry) => {
					result.push(entry.portTag);
				}, this);
			}
			else {
				let entry = <BridgePortSelectorEntry>this.selectedPorts;
				result.push(entry.portTag);
			}
		}
		return result;
	}

	public get totalPortCount(): number {
		if (this.entries)
			return this.entries.length;
		else
			return 0;
	}

	public get selectedPortCount(): number {
		if (this.selectedPorts) {
			if (Array.isArray(this.selectedPorts)) {
				return this.selectedPorts.length;
			}
			else {
				return 1;
			}
		}
		return 0;
	}

}
